// The Relay agent builder at /admin/tools. `ui` is the copy of the embedded agentic-ui
// components, keyed as they ask for it; a test pins it to the library's catalogue.
export const agentBuilder = {
  newAgentName: 'Untitled agent',
  page: {
    tools: 'Tools catalog',
  },
  category: {
    label: 'Tools hub category',
    none: 'No category',
    plan: 'Plan',
    create: 'Create',
    scale: 'Scale',
    deliver: 'Deliver',
    support: 'Support',
  },
  details: {
    imageDefaults: {
      label: 'Image defaults',
      hint: 'What a new chat starts with. People can change it per message.',
    },
    listing: 'Listing',
    chat: 'In the chat',
    knowledgeBases: 'Knowledge base picker',
    knowledgeBasesHint: 'Let people pick knowledge bases for the agent to search.',
    suggestions: 'Suggested replies',
    suggestionsHint:
      'Offer a few replies to pick after each answer, for an agent people talk to over several turns.',
    models: 'Models people can pick',
    modelsHint:
      'People pick one of these in the chat. New models in a tier are offered as they arrive.',
    modelMode: {
      label: 'Model',
      fixed: 'Fixed model',
      choose: 'People choose',
    },
    runsOn: 'Runs on',
    runsOnHint: 'The agent always answers with this model.',
    files: {
      saveFirst: 'Add files once the agent is saved.',
      remove: 'Remove {{fileName}}',
    },
    description: 'Description',
    descriptionHint: 'What the agent does, shown where people pick it.',
    greeting: 'Greeting',
    greetingHint: 'The first message people see.',
    placeholderPrompt: 'Prompt hint',
    placeholderPromptHint: 'The hint in an empty message box.',
    tier: {
      nano: 'Nano',
      mini: 'Mini',
      standard: 'Standard',
      flagship: 'Flagship',
    },
  },
  list: {
    unpublishedChanges: 'Unpublished changes',
  },
  toolbar: {
    name: 'Agent name',
    scope: 'Available to: {{scope}}',
    history: 'History',
    more: 'More actions',
    publish: 'Publish v{{version}}',
  },
  view: {
    label: 'Builder view',
    widgets: 'Widgets',
    build: 'Agents',
    test: 'Test',
    testSideLabel: 'Beside the test chat',
    structure: 'Structure',
    runDetails: 'Run details',
    widget: 'Widget',
    data: 'Data',
  },
  saveStatus: {
    new: 'Not saved yet',
    saved: 'Saved',
    unsaved: 'Unsaved changes',
    saving: 'Saving…',
    retrying: 'Save failed, retrying',
    failed: 'Could not save',
    failedBecause: 'Could not save: {{reason}}',
    conflict: 'Changed elsewhere',
  },
  status: {
    draft: 'Draft',
    published: 'Published',
    publishedVersion: 'Published v{{version}}',
    unpublished: 'Unpublished',
    archived: 'Archived',
  },
  actions: {
    cancel: 'Cancel',
  },
  scope: {
    title: 'Who can use this agent',
    description:
      'Everyone in the scope must be able to use every tool, agent and MCP server in the flow, or publishing is refused.',
    option: {
      org: { label: 'Organization', hint: 'Everyone in this organization.' },
      workspace: { label: 'Some workspaces', hint: 'Members of the workspaces you pick.' },
      user: { label: 'Only me', hint: 'Only you can see and use it.' },
    },
    workspaces: 'Workspaces',
    workspacesPlaceholder: 'Pick workspaces',
    workspaceCount_one: '{{count}} workspace',
    workspaceCount_other: '{{count}} workspaces',
    orgExceptCount_one: 'Organization except {{count}} workspace',
    orgExceptCount_other: 'Organization except {{count}} workspaces',
    dependents: {
      title: 'Other agents depend on this one',
      description:
        'Published agents call this one, and this scope would leave their users without it. Change or unpublish them first:',
      hidden_one: 'and {{count}} agent you cannot see',
      hidden_other: 'and {{count}} agents you cannot see',
    },
    ownerOnly: 'Only the owner can make an agent private to one person.',
    failed: 'The scope could not be saved. Try again.',
    save: 'Save scope',
  },
  problems: {
    none: 'No problems',
    count_one: '{{count}} problem',
    count_other: '{{count}} problems',
    structural: 'The flow is not complete',
    reachability: 'Not everyone in the scope can use this',
  },
  issues: {
    entryNotInlineAgent: 'The entry point must be an agent defined in this flow.',
    unsupportedNode: 'Scorer nodes cannot be published yet.',
    referenceCycle: 'Agents call each other as tools in a loop.',
    referenceTooDeep: 'Agents call each other too many levels deep.',
    workspacesRequired: 'Pick at least one workspace.',
    agentSettingsInvalid: 'This agent cannot run with these settings. Pick them again.',
    invalidFlow: 'Some of these settings are not valid.',
    knowledgeBaseNotOrganizationWide:
      'This knowledge base is shared with specific workspaces only, so an agent for the whole organization cannot use it. Share the agent only with those workspaces, or share the knowledge base with the whole organization.',
    knowledgeBaseNotInWorkspaces:
      'This knowledge base is not shared with {{workspaces}}, so the agent could not read it there. Share it with them, or share the agent only with the workspaces the knowledge base is shared with.',
    otherWorkspaces_one: '{{count}} other workspace',
    otherWorkspaces_other: '{{count}} other workspaces',
    widgetNeedsApproval: 'A widget cannot ask for approval. Set it to Allow or Deny.',
    widgetUnavailable: 'This widget is archived, deleted or not shared with you.',
    widgetUnreachableInScope:
      '"{{widget}}" is shared with fewer people than this agent. Share the widget at least as widely as the agent.',
    widgetNotATemplate:
      '"{{widget}}" is not ready to publish. Its checks in the widget builder say what to fix.',
  },
  publish: {
    widgets: 'Widgets published with it',
    widgetNew: '{{name}}, v{{version}} (new)',
    widgetChanged: '{{name}}, v{{version}}: {{changes}}',
    widgetOthers_one: '{{agents}} keeps its version until it is published again',
    widgetOthers_other: '{{agents}} keep their versions until they are published again',
    title: 'Publish version {{version}}',
    description:
      'Chats with this agent start using this version once it is published. Available to: {{scope}}.',
    note: 'What changed (optional)',
    notePlaceholder: 'For the version history',
    confirm: 'Publish v{{version}}',
    archived: 'Restore the agent before publishing it.',
    failed: 'The agent could not be published. Try again.',
    chooseScopeDescription:
      'Pick who can use this agent. Chats with it start using this version once it is published.',
    changeScope: 'Change who can use it',
  },
  history: {
    title: 'History',
    description: 'Every published version, and who changed what.',
    versions: 'Versions',
    audit: 'Activity',
    noVersions: 'Nothing has been published yet.',
    version: 'Version {{version}}',
    live: 'Live',
    preview: 'Preview',
    publishAgain: 'Publish again',
    previewFailed: 'That version could not be opened. Try again.',
    republishFailed: 'That version could not be published again. Try again.',
    action: {
      created: 'Created',
      published: 'Published',
      unpublished: 'Unpublished',
      scopeChanged: 'Scope changed',
      archived: 'Archived',
      restored: 'Restored',
    },
  },
  lifecycle: {
    unpublish: 'Unpublish',
    archive: 'Archive',
    restore: 'Restore',
    hasDependents: 'Other published agents use this one. Change them first.',
    failed: 'That change could not be saved. Try again.',
  },
  conflict: {
    title: 'Someone else changed this draft',
    description: 'Load their version, or keep yours and save it over theirs.',
    loadLatest: 'Load latest',
    keepMine: 'Keep mine',
  },
  notFound: {
    title: 'Agent not found',
    description: 'It does not exist, or you cannot see it.',
  },
  testDock: {
    compactFailed: 'The conversation could not be summarized. Try again.',
    noGreeting: 'No greeting yet. People see it when they open a chat with this agent.',
    greetingSetIn: "The greeting is set in the agent's settings.",
    openSettings: 'Open agent settings',
    notSavedTitle: 'Nothing to test yet',
    notSaved: 'Add an agent on the canvas. It saves as a draft you can test here.',
    goToBuild: 'Go to Build',
    sessionTitle: 'Test: {{name}}',
    notSent: 'Your latest edits are not saved, so the message was not sent. Try again.',
  },
  dataTab: {
    label: 'Data the agent holds',
    datasets: 'Datasets',
    conversation: 'Saved by the agent',
    noConversation: 'Send a test message to see what the agent saves.',
    nothingKept: 'Nothing saved yet. What the agent keeps with save_data shows here.',
    empty: 'This agent has no datasets yet. Send a test message to see what it saves.',
    saved: 'Saved',
    dataset: 'Dataset',
    summary: '{{format}}, {{size}}: {{shape}}',
    truncated: 'Cut to 1 MB',
    loadFailed: 'Could not load this entry.',
    loading: 'Loading…',
    format: {
      json: 'JSON',
      csv: 'CSV',
      tsv: 'TSV',
      markdown: 'Markdown',
      text: 'Text',
    },
  },
  copilot: {
    intro: {
      title: 'Flow.Assist',
      description:
        "Describe what you want to build and I'll draw it on the canvas: agents, connections, tools and prompts.",
    },
    handOff: {
      answering: '{{name}} is answering',
      back: 'Back to Flow.Assist',
    },
    sessionTitle: 'Flow.Assist: {{name}}',
    notSent: 'The message could not be sent to Flow.Assist. Try again.',
  },
  knowledgeSources: {
    foundational: 'Foundational',
    organization: 'Organization-wide',
    workspace: 'One workspace',
    files_one: '{{count}} file',
    files_other: '{{count}} files',
    guidelines_one: '{{count}} guideline',
    guidelines_other: '{{count}} guidelines',
    guidelinesNeedOneWorkspace:
      'A guidelines folder belongs to one workspace. Share this agent with that workspace only to add one.',
  },
  widgets: {
    page: {
      data: 'Data',
      checks: 'Checks',
      actions: 'Actions',
      versions: 'Versions',
      schema: 'Data schema',
      samples: 'Samples',
      noSamples: 'No sample data yet',
    },
    stage: {
      question: 'How did the campaign do last quarter?',
      reply: 'Here is the summary.',
      width: '{{width}}px wide',
      resizeLeft: 'Resize the widget from the left',
      resizeRight: 'Resize the widget from the right',
    },
    info: {
      usedBy: 'Used by',
      unused: 'No agent uses it yet.',
    },
    node: {
      open: 'Open in Widgets',
      remove: 'Remove from agent',
      usedBy_one: 'Used by {{count}} agent',
      usedBy_other: 'Used by {{count}} agents',
    },
    view: {
      onThisAgent: 'On this agent',
      publishesWithAgent: 'Publishes with the agent',
      show: 'Show widgets',
      hide: 'Hide widgets',
      checksPass: 'Checks pass',
      problems_one: '{{count}} problem',
      problems_other: '{{count}} problems',
      addToAgent: 'Add to this agent',
      more: 'More',
      empty: 'Pick a widget, or ask Flow.Assist for a new one.',
      unavailable: "This widget can't be opened.",
    },
    placement: {
      inline: 'Inline, in the reply',
      panel: 'Side panel',
    },
    status: {
      draft: 'Draft',
      changed: 'v{{version}}, unpublished changes',
      published: 'Published v{{version}}',
    },
    preview: {
      empty: 'The Widget agent has not written this widget yet.',
      theme: 'Preview theme',
      sample: 'Sample data',
      light: 'Light',
      dark: 'Dark',
    },
    actions: {
      none: 'This widget has no buttons. Ask the Widget agent to add one that asks a follow-up.',
      filled: 'With "{{sample}}": {{message}}',
      type: {
        send: 'Sends a message',
        prefill: 'Fills the message box',
        'open-panel': 'Opens the side panel',
        'set-filter': 'Sets a filter',
      },
    },
    checks: {
      ready: 'Ready to publish',
      line: 'Line {{line}}: {{message}}',
      pending: 'Checking…',
      state: {
        pass: 'Passed',
        warn: 'Worth a look',
        fail: 'Needs a fix',
        pending: 'Checking',
      },
      template: {
        title: 'Reads its data',
        passed: 'Every value comes from the data; nothing from a sample is written in.',
      },
      samples: {
        title: 'Sample data',
        passed_one: '{{count}} sample, it fits the schema.',
        passed_other: '{{count}} samples, all fit the schema.',
      },
      empty: { title: 'Missing data', passed: 'Shows an empty state where data is missing.' },
      html: { title: 'Safe HTML, Tessra classes', passed: 'Nothing removed by the sanitizer.' },
      charts: { title: 'Charts', passed: 'Every chart spec is valid and draws.' },
      actions: { title: 'Actions', passed: 'Every button is declared, with a known type.' },
      fit: {
        title: 'Fits the chat',
        passed: 'Fits {{widths}}.',
        tooWide: 'Too wide at {{widths}}: something scrolls sideways.',
      },
      images: {
        title: 'Images',
        passed: 'Every image shows.',
        failed: 'Could not show {{assets}}.',
      },
      other: { title: 'Other' },
    },
    delete: {
      label: 'Delete widget',
      warning:
        'The widget and all its versions are deleted for good. Chats that already showed it keep what they showed.',
    },
    versions: {
      none: 'Not published yet',
      label: 'v{{version}}',
      untitled: 'Version {{version}}',
      live: '{{date}} · live',
      preview: 'Preview',
      previewLabel: 'Preview v{{version}}',
      restore: 'Restore',
      restoreLabel: 'Restore v{{version}}',
      previewing: 'Previewing v{{version}}',
      backToDraft: 'Back to the draft',
      hint: 'Restore copies a version into the draft. It goes live, as a new version, when you publish an agent that shows it.',
      restoreTitle: 'Restore v{{version}} into the draft?',
      restoreDescription:
        'The draft becomes v{{version}} as it was published, replacing changes not yet published. Each agent keeps the version it was published with until you publish it again.',
      restoreFailed: 'Could not restore this version. Reload and try again.',
    },
    publish: {
      lines_one: '{{count}} line changed',
      lines_other: '{{count}} lines changed',
      adds: 'adds {{fields}}',
      removes: 'removes {{fields}}',
      movesInline: 'moves inline, into the reply',
      movesPanel: 'moves to the side panel',
      samples: 'new sample data',
    },
    newWidgetName: 'New widget',
    newWidgetPrompt: 'Create a new widget that shows ',
    thumbnail: {
      alt: 'Preview of the widget',
    },
  },
  ui: {
    chat: {
      compactionDetail:
        'Turns 1 to {{turns}} summarized: read {{input}} tokens, {{cached}} from cache, wrote {{output}}.',
      compactionMarker: 'Earlier messages summarized',
      answeredQuestions: 'You answered',
      askingAnnouncement: 'The agent is asking you something. Answer above the message box.',
      minimizeQuestions: 'Minimize questions',
      nextQuestion: 'Next',
      otherAnswer: 'Other',
      otherAnswerHint: 'Type your own answer',
      previousQuestion: 'Back',
      questionProgress: '{{current}}/{{total}}',
      questions: 'Questions from the agent',
      sendAnswers: 'Send answers',
      showQuestions: 'Show questions',
      skipQuestions: 'Skip',
      unansweredQuestions: 'Not answered',
      yourAnswer: 'Your answer',
    },
    builder: {
      palette: 'Palette',
      sidePanel: 'Run details',
    },
    canvas: {
      allTools: 'Every tool it offers',
      alwaysAllowedCount_one: '{{count}} always allowed',
      alwaysAllowedCount_other: '{{count}} always allowed',
      approvalCount_one: '{{count}} needs approval',
      approvalCount_other: '{{count}} need approval',
      emptyHint: 'Drag one in from the palette.',
      emptyHintCopilot:
        'Drag one in from the palette, or ask Flow.Assist to draw the whole flow for you.',
      emptyTitle: 'Start with an agent',
      loopRefused: 'Not connected: this would let an agent invoke itself ({{loop}}).',
      noCriterion: 'No criterion yet',
      noScorer: 'No scorer picked',
      noToolsEnabled: 'No tools enabled',
      nothingToShow: 'Nothing to show',
      resizeHeight: 'Resize panel height',
      resizeSplit: 'Resize between the inspector and Flow.Assist',
      resizeWidth: 'Resize panel width',
      toolCount_one: '{{count}} tool',
      toolCount_other: '{{count}} tools',
      toolCountOne_one: '{{count}} tool',
      toolCountOne_other: '{{count}} tool',
      webSearchProvider: "the model's provider",
      agentFiles: "This agent's files",
    },
    completeness: {
      addCriterion: 'Add a criterion',
      addInstructions: 'Add instructions',
      addName: 'Add a name',
      entryNotAgent: 'The entry node must be an agent',
      loop: 'Agents invoke each other in a loop: {{loop}}',
      missingAgent: 'Missing agent reference',
      missingConnection: 'Missing connection',
      missingTool: 'Missing tool reference',
      nameFlow: 'Name the flow',
      noToolsSelected: 'No tools selected',
      nodeNeedsAttention_one: '{{count}} node needs attention',
      nodeNeedsAttention_other: '{{count}} node needs attention',
      nodesNeedAttention_one: '{{count}} node needs attention',
      nodesNeedAttention_other: '{{count}} nodes need attention',
      pickScorer: 'Pick a scorer',
      refToolSteps: 'A referenced agent runs the tool-step budget it was registered with',
      setEntry: 'Set an entry agent',
      toolStepsRange: 'Tool steps must be a whole number from 1 to {{max}}',
      unknownTool: 'Unknown tool',
      unreachable: 'Never runs: nothing from the entry agent reaches it',
      pickGuidelinesFolder: 'Choose a guidelines folder',
      pickKnowledgeBase: 'Choose a knowledge base',
    },
    composer: {
      attach: 'Attach file',
      attachDisabled: 'Attachments are not allowed',
      placeholder: 'Message the agent…',
      removeFile: 'Remove {{name}}',
      send: 'Send',
      stop: 'Stop',
      tooLarge: 'Too large to send: {{files}}',
      tooMany_one: 'At most {{count}} file can be sent at once: {{files}}',
      tooMany_other: 'At most {{count}} files can be sent at once: {{files}}',
      unsupportedType: 'This type of file cannot be sent: {{files}}',
    },
    connector: {
      allTools: 'Every tool',
      allToolsHint:
        "Offer whatever the server has when the agent runs. Tools it doesn't mark read-only ask before running.",
      alwaysAllow: 'Always allow',
      alwaysAllowAllTitle: 'Allow every tool?',
      alwaysAllowAllWarningOne_one:
        '{{count}} of these tools may change things outside the agent. It will run without asking anyone who uses this agent.',
      alwaysAllowAllWarningOne_other:
        '{{count}} of these tools may change things outside the agent. They will run without asking anyone who uses this agent.',
      alwaysAllowAllWarning_one:
        '{{count}} of these tools may change things outside the agent. It will run without asking anyone who uses this agent.',
      alwaysAllowAllWarning_other:
        '{{count}} of these tools may change things outside the agent. They will run without asking anyone who uses this agent.',
      alwaysAllowCancel: 'Cancel',
      alwaysAllowConfirm: 'Always allow',
      alwaysAllowTitle: 'Always allow {{tool}}?',
      alwaysAllowWarning:
        'This tool may change things outside the agent. It will run without asking anyone who uses this agent.',
      alwaysAllowed: 'Always allowed',
      alwaysAllowedTool: "The server doesn't mark this tool read-only, but it runs without asking",
      connect: 'Connect',
      discovering: 'Discovering tools…',
      noTools: 'This connection exposes no tools.',
      notConnected: 'Connect this server with your own account to choose its tools.',
      retry: 'Retry',
      setAll: 'Set all tools',
      tools: 'Tools',
      unreachable: "Couldn't reach the MCP server.",
      writeTool: "The server doesn't mark this tool read-only, so it asks before running",
    },
    copilot: {
      minimize: 'Minimize Flow.Assist',
      empty:
        "Describe what you want to build and I'll draw it on the canvas: agents, connections, tools and prompts.",
      newConversation: 'New conversation',
      step: {
        addAgent: 'Added agent {{name}}',
        addGuardrail: 'Added a guardrail',
        addScorer: 'Added a scorer',
        connect: 'Connected two nodes',
        connection: 'Updated connector tools',
        connector: 'Attached connector {{name}}',
        delete: 'Deleted a node',
        disconnect: 'Removed a connection',
        edge: 'Updated a connection',
        entry: 'Set the entry agent',
        guardrail: 'Updated a guardrail',
        metadata: 'Updated agent details',
        model: 'Changed the model',
        newFlow: 'Started a new flow',
        policy: 'Changed a tool policy',
        prompt: 'Wrote the system prompt',
        redo: 'Redid a change',
        dataset: 'Kept {{key}} as a dataset',
        datasetRemoved: 'Removed the dataset {{key}}',
        rename: 'Renamed to {{name}}',
        save: 'Saved the flow',
        scorer: 'Updated a scorer',
        settings: 'Changed agent settings',
        tool: 'Attached tool {{name}}',
        undo: 'Undid the last change',
        webSearch: 'Attached web search',
      },
      open: 'Ask Flow.Assist to build or change this agent',
      placeholder: 'Describe what to build…',
      defaultModel: 'Default model',
      model: 'Flow.Assist model',
      title: 'Flow.Assist',
    },
    trace: {
      artifactKind: 'Shown as',
      conditionalApproval: 'conditional approval',
      cost: 'Cost',
      duration: 'Duration',
      error: 'error',
      guardrailType: 'Type',
      input: 'Input',
      kind: {
        agent: 'agent',
        artifact: 'widget',
        guardrail: 'guardrail',
        handoff: 'handoff',
        step: 'step',
        tool: 'tool',
      },
      kept: 'kept as {{handle}}',
      phase: {
        design: 'writing',
        fix: 'fixing',
        render: 'render check',
      },
      phaseTime: '{{phase}} {{seconds}}s',
      messages: 'Messages',
      no: 'No',
      opaque:
        'A system agent: only that it ran is shown. Its tools, steps and prompts stay private, and what it put on screen appears as its own step.',
      output: 'Output',
      reason: 'Reason',
      requiresApproval: 'requires approval',
      response: 'Response',
      running: 'running…',
      systemPrompt: 'System prompt',
      tokens: 'Tokens',
      tokensCached: '{{input}} in / {{output}} out · {{cached}} cached',
      tokensValue: '{{input}} in / {{output}} out',
      tools: 'Tools',
      triggered: 'Triggered',
      timeSpent: 'Time spent',
      tripped: 'tripped',
      waiting: 'waiting',
      yes: 'Yes',
    },
    debug: {
      compactNow: 'Compact now',
      compacting: 'Compacting...',
      agents_one: '{{count}} agent',
      agents_other: '{{count}} agents',
      detail: {
        cost: 'Cost',
        model: 'Model',
        tokens: 'Tokens',
      },
      empty: 'Send a test message to see which part of the agent runs.',
      guardrail: {
        failed: 'Blocked',
        passed: 'Passed',
      },
      handoffs_one: '{{count}} handoff',
      handoffs_other: '{{count}} handoffs',
      kiloTokens: '{{value}}k',
      context: 'Context {{tokens}}',
      contextOfWindow: 'Context {{tokens}} / {{window}} ({{percent}}%)',
      contextTitle: 'The largest prompt {{agent}} sent its model this turn',
      megaTokens: '{{value}}M',
      kind: {
        handoff: 'handoff',
        tool: 'as tool',
      },
      seconds: '{{value}}s',
      status: {
        aborted: 'Stopped',
        awaiting: 'Waiting for you',
        completed: 'Completed',
        error: 'Failed',
        running: 'Running',
      },
      title: 'Run details',
      tokensCached: '{{cached}} cached',
      tokensInOut: '{{input}} in · {{output}} out',
      unnamedTool: 'tool',
    },
    edge: {
      asTool: 'as tool',
      denied: 'denied',
      handoff: 'handoff',
      input: 'input',
      output: 'output',
      remove: 'Remove link',
      scores: 'scores',
      tool: 'tool',
    },
    entity: {
      widget: 'Widget',
      generativeWidget: 'Generative widgets',
      widgetInline: 'Inline widget',
      widgetPanel: 'Side panel widget',
      agent: 'Agent',
      connector: 'Connector',
      guardrail: 'Guardrail',
      refAgent: 'Existing agent',
      scorer: 'Scorer',
      tool: 'Tool',
      webSearch: 'Web Search',
      workflow: 'Workflow',
      brandGuidelines: 'Brand guidelines',
      knowledgeBase: 'Knowledge base',
    },
    graph: {
      handoffFrom: 'Handoff from {{name}}',
      requiresApproval: 'requires approval',
      requiresApprovalConditional: 'requires approval (conditional)',
    },
    guidelineCategory: {
      brandAssets: 'Brand assets',
      contentStyle: 'Content style',
      technicalRequirements: 'Technical requirements',
      textCorrectness: 'Text correctness',
      textFormatting: 'Text formatting',
    },
    inspector: {
      problems: {
        title: 'Needs attention',
      },
      agent: {
        advanced: 'Advanced',
        samplingOneOf:
          'This model takes one of temperature and top P, so setting one clears the other.',
        defaultModel: 'Default model',
        instructions: 'Instructions',
        isEntry: 'This agent is the flow entry point.',
        llmFacing: 'LLM-facing',
        maxOutputTokens: 'Max output tokens',
        maxToolSteps: 'Max tool steps',
        maxToolStepsDefault: '{{steps}} (default)',
        maxToolStepsHint: 'Rounds of tool calls before the agent has to answer.',
        maxTurns: 'Max turns',
        memory: 'Memory',
        metadata: 'Metadata',
        model: 'Model',
        modelSection: 'Model & sampling',
        refReadOnly: 'Referenced agents are read-only; edit them where they are defined.',
        setEntry: 'Set as entry',
        temperature: 'Temperature',
        topP: 'Top P',
        useMemory: 'Use memory',
        useMemoryHint: 'Keeps the latest turns of the conversation in front of the agent.',
        userFacing: 'User-facing',
      },
      agentTool: {
        title: 'Agent as tool',
        toolName: 'Tool name',
      },
      close: 'Close inspector',
      criterion: 'Criterion',
      description: 'Description',
      edge: {
        route: '{{source}} → {{target}}',
      },
      empty: 'Create or open a flow to begin',
      guardrail: {
        criterionRequired: 'A criterion is required to run.',
        flowDefault: 'Flow default',
      },
      guards: {
        input: 'Input',
        output: 'Output',
        placement: 'Placement',
        placementHint:
          "Input evaluates the incoming message before the agent runs; output evaluates the agent's response.",
        title: 'Guardrail link',
      },
      handoff: {
        placeholder: 'Hand off to {{name}}',
        title: 'Handoff',
      },
      judgeModel: 'Judge model',
      minimize: 'Minimize inspector',
      restore: 'Show the inspector',
      name: 'Name',
      policy: {
        allow: 'Allow',
        deny: 'Deny',
        needsApproval: 'Needs approval',
      },
      reasoning: {
        high: 'High',
        low: 'Low',
        medium: 'Medium',
        minimal: 'Minimal',
        label: 'Reasoning',
        max: 'Max',
        xhigh: 'Extra high',
      },
      sampling: {
        auto: 'Auto',
        reset: 'Reset',
      },
      scorer: {
        default: 'Scorer default',
        expectedTools: 'Expected tools',
        expectedToolsHint:
          'Comma-separated. Scores the fraction of these the agent actually called.',
        expectedToolsPlaceholder: 'web_search, calculate',
        judgeModelHint:
          "Scorer models resolve through the scoring library's own client, not the engine model catalog, so eval spend does not appear in the cost panel.",
        needsCriterion:
          '{{scorer}} needs a criterion; without one it has nothing to grade the output against.',
        needsTools:
          '{{scorer}} needs at least one expected tool; with nothing to look for it cannot measure anything, and reports no score at all.',
        pick: 'Pick a scorer…',
        referenceBased:
          'Reference-based: needs an {{field}} value in the dataset, so it only runs in offline evaluation.',
        required: 'A scorer is required to run.',
        resultsKey:
          'Results are reported under the name above, or under the scorer id if you leave it blank.',
        scorer: 'Scorer',
      },
      scorerPlaceholder: 'scorer',
      scores: {
        body: 'Results are reported under {{key}}. Scoring is post-hoc: unlike a guardrail, it never blocks the run. Configure the scorer on its own node.',
        title: 'Scorer link',
      },
      title: 'Inspector',
      webSearch: {
        allowedDomains: 'Only search these sites',
        appliesTo: 'Applies on {{providers}}',
        blockedDomains: 'Never search these sites',
        blockedPlaceholder: 'reddit.com',
        blockedWithAllowed: 'Anthropic takes one list only, so it uses the sites to search.',
        city: 'City',
        connected: 'The connected agents run on {{providers}}.',
        contextHigh: 'High',
        contextLow: 'Low',
        contextMedium: 'Medium',
        contextSize: 'How much of each page to read',
        contextSizeHint: 'More context answers better, and costs more and takes longer.',
        country: 'Country',
        countryInvalid: 'Use a two-letter country code, such as GB.',
        countryPlaceholder: 'Country (GB)',
        default: 'Default',
        disclaimer:
          "The search is run by the provider of the connected agent's model, so each setting applies only on the providers it names, and the others ignore it.",
        domainsHint: 'Separate sites with commas. Subdomains are included.',
        domainsPlaceholder: 'apple.com, reuters.com',
        ignored: 'Ignored while the connected agents run on {{providers}}.',
        liveResults: 'Fetch live pages',
        liveResultsHint: 'Off uses cached results, which is faster but can be out of date.',
        location: 'Search from a location',
        locationHint: 'Ranks results for that place. All fields are optional.',
        maxUses: 'Most searches per answer',
        maxUsesHint: 'Leave empty for no limit.',
        notConnected: 'Connect it to an agent to see which provider runs it.',
        region: 'Region',
        timeRange: 'Only results published between',
        timeRangeEnd: 'To',
        timeRangeHint: 'Set both dates, or neither.',
        timeRangeStart: 'From',
        timezone: 'Time zone',
        timezonePlaceholder: 'Europe/London',
        provider: {
          openai: 'OpenAI',
          anthropic: 'Anthropic',
          google: 'Gemini',
        },
      },
      tool: {
        policy: 'Policy',
        policyHint:
          'Deny keeps the connection but removes the tool from the agent (shown as a severed edge).',
      },
      toolLink: {
        body: 'Tool identity and policy are configured on the tool node.',
        title: 'Tool link',
      },
      toolConfig: {
        categories: 'Categories',
        categoriesHint: 'Leave all unticked to read every category.',
        change: 'Change',
        placements: 'Where widgets show',
        placementsHint: 'Inline for short summaries, the side panel for dashboards.',
        placementInline: 'Inline, in the reply',
        placementPanel: 'Side panel',
        createInKnowledgeHub: 'Create one in the Knowledge Hub',
        gone: 'No longer available. Pick another.',
        guidelinesFolder: 'Guidelines folder',
        guidelinesHint:
          'Read with the access of whoever uses the agent. A folder belongs to one workspace.',
        knowledgeBase: 'Knowledge base',
        knowledgeBaseHint: 'Anyone who uses this agent can search it.',
        noFolders: 'No guidelines folders in this workspace yet.',
        noKnowledgeBases: 'No knowledge bases yet.',
        noMatch: 'Nothing matches.',
        open: 'Open',
        search: 'Search',
        agentFiles: "This agent's files",
        agentFilesHint: 'Only this agent searches them. They apply to every version straight away.',
        pickKnowledgeBase: 'Knowledge base',
        uploadFiles: 'Upload files',
      },
    },
    maximize: {
      maximizeLabel: 'Maximize {{label}}',
      maximizeTitle: 'Maximize',
      restoreLabel: 'Restore {{label}}',
      restoreTitle: 'Restore (Esc)',
    },
    messages: {
      file: 'file',
    },
    model: {
      configuration: 'Configuration',
      maxTokens: 'max tokens',
      price: '{{input}} / {{output}} per 1M',
      providerLogo: '{{provider}} logo',
      temperature: 'temp',
      topP: 'top-p',
    },
    node: {
      entry: 'entry',
      entryPoint: 'Entry point',
      handle: {
        handoffs: 'Handoffs',
        input: 'Input',
        inputGuardrails: 'Input guardrails',
        outputGuardrails: 'Output guardrails',
        scorers: 'Scorers',
        tools: 'Tools',
      },
      policy: {
        approval: 'approval',
        denied: 'denied',
      },
      rename: 'Double-click to rename',
      run: {
        kiloTokens: '{{value}}k tok',
        seconds: '{{value}}s',
        tokens: '{{value}} tok',
      },
    },
    palette: {
      deleteWidget: 'Delete {{name}}',
      generativeWidgets: 'Designed by the agent, answer by answer',
      widgetInfo: 'About {{name}}',
      newWidget: 'New widget',
      widgets: 'Widgets',
      agent: 'Agent',
      category: {
        agents: 'Agents',
        connectors: 'Connectors',
        guardrails: 'Guardrails',
        scorers: 'Scorers',
        tools: 'Tools',
        widgets: 'Widgets',
      },
      connectors: 'Connectors',
      existingAgents: 'Existing agents',
      guardrail: 'Guardrail',
      manageConnections: 'Manage connections',
      minimize: 'Minimize palette',
      restore: 'Show the palette',
      noMatches: 'No matches.',
      scorer: 'Scorer',
      searchLabel: 'Search palette',
      searchPlaceholder: 'Search tools, agents, connectors',
      title: 'Palette',
      tools: 'Tools',
      webSearch: 'Web Search',
      webSearchDescription:
        "Searches the web for current information, through the agent's model provider.",
      brand: 'Brand',
      knowledge: 'Knowledge',
    },
    widget: {
      draft: 'Draft',
      placement: {
        inline: 'Inline',
        panel: 'Panel',
      },
      version: 'v{{version}}',
      versionChanged: 'v{{version}}, changed',
    },
  },
} as const;
